import { faCircleQuestion, faUpload, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { ChangeEvent, FormEvent, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { isIDNotSet, StringID } from "userful-chronos-app-common-js/dist/models/common";
import {
    httpsActions,
    sshUsersActions,
    useConfigDispatch,
    useConfigSelector,
} from "userful-chronos-common-store/dist/config";
import { HelpInfo } from "../sourceassets";
import { UserfulConfirmationDialog } from "../widgets";
import "./HttpsForm.scss";
import OptionContainer from "../option/optionContainer/OptionContainer";
import Button from "../button/Button";
import Note from "../note/Note";
import { FormBlock } from "../form";

const customStyles = {
    option: (provided, state) => ({
        ...provided,
        color: "#1F2747",
        maxHeight: "176px",
        padding: "8px 16px",
        background: state.isSelected ? "#F3F4F5" : state.isFocused ? "#F3F4F5" : undefined,
        marginBottom: "2px",
        "&:active": {
            background: "#F3F4F5",
        },
    }),
    control: (base, state) => ({
        // none of react-select's styles are passed to <Control />
        ...base,
        width: "100%",
        height: "36px",
        color: "#1F2747",
        background: " #FFFFFF",
        boxShadow: "inset 0px 2px 4px rgba(31, 39, 71, 0.1)",
        borderRadius: "8px",
        transition: "none",
        display: "flex",
        minHeight: "36px",
        border: state.menuIsOpen ? "1px solid #6C89E8" : "1px solid #C6C8CE",
        "&:hover": {
            border: "1px solid #4B5168",
        },
    }),

    dropdownIndicator: (base) => ({
        ...base,
        color: "#4B5168", // Custom colour
        fontWeight: 900,
        fontSize: "16px",
    }),
    valueContainer: (base) => ({
        ...base,
        display: "flex",
        padding: "0px 16px",
    }),
};

interface IProps {
    onSubmit?: (e: FormEvent) => void;
    /**
     * Flag to indicate initial setup flow; creation-only controls should only appear when true.
     */
    isInit?: boolean;
    formId?: string;
    sshUserId?: StringID;
}

export default function HttpsForm({ onSubmit, isInit = false, formId = "https-form", sshUserId }: IProps) {
    const { t } = useTranslation();

    const dispatch = useConfigDispatch();

    const systemID = getGlobalStates().systemID;

    const settings = useConfigSelector((state) => state.httpsStore.settings);
    const errors = useConfigSelector((state) => state.httpsStore.errors);
    const popUpError = useConfigSelector((state) => state.httpsStore.popUpError);
    const defaultSSHUserEnable = useConfigSelector((state) => state.httpsStore.defaultSSHUserEnable);

    const [keyfocus, setKeyFocus] = useState(false);
    const [certFocus, setCertFocus] = useState(false);

    const keyRef = useRef<HTMLInputElement>(null);
    const certRef = useRef<HTMLInputElement>(null);

    const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.name === "key" && keyRef.current) {
            setKeyFocus(true);
            setTimeout(() => keyRef.current.select(), 100);
        } else if (certRef.current) {
            setCertFocus(true);
            setTimeout(() => certRef.current.select(), 100);
        }
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.name === "key") {
            setKeyFocus(false);
        } else {
            setCertFocus(false);
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(
            httpsActions.updateSettings({
                [e.target.name]: e.target.value,
            }),
        );
    };

    const resolvedSshUserId: StringID | null = useMemo(() => {
        if (sshUserId && !isIDNotSet(sshUserId)) {
            return sshUserId;
        }
        if (settings.sshUser?.id && !isIDNotSet(settings.sshUser.id)) {
            return settings.sshUser.id;
        }
        if (systemID) {
            return { value: systemID };
        }
        return null;
    }, [settings.sshUser?.id, sshUserId?.value, systemID]);

    const onToggleAutoGenerateCert = () => {
        dispatch(httpsActions.toggleAutoGenerateCert());
        dispatch(httpsActions.sanitizeSettings());
    };

    const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (fileEvent) => {
                dispatch(
                    httpsActions.updateSettings({
                        [e.target.name]: fileEvent.target.result,
                    }),
                );
                dispatch(httpsActions.sanitizeSettings());
                if (e.target.name === "key" && keyRef.current) {
                    keyRef.current.focus();
                    setTimeout(() => keyRef.current.select(), 100);
                } else if (certRef.current) {
                    certRef.current.focus();
                    setTimeout(() => certRef.current.select(), 100);
                }
            };
            reader.onerror = (error) => console.log("Upload file failed ", error);
            reader.readAsText(file, "UTF-8");
        }
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (errors.frontEndUrl || errors.cert || errors.key) {
            return;
        }
        if (!defaultSSHUserEnable && settings.sshUser.userfulKeys && resolvedSshUserId) {
            dispatch(sshUsersActions.ensureDefaultUser(resolvedSshUserId));
        }
        if (onSubmit) {
            onSubmit(e);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSubmit(e);
        }
    };

    const getShowValue = (value: string, focused: boolean): string => (focused || !value ? value : "******");

    const deploymentTypeOptions = [
        {
            value: "ONPREM",
            label: t("CommonUI.Config.deploymentOnPrem"),
        },
        {
            value: "CLOUD",
            label: t("CommonUI.Config.deploymentCloud"),
        },
    ];
    const selectedDeployment =
        deploymentTypeOptions.find((i) => i.value === settings.serverDeploymentType) || deploymentTypeOptions[0];

    const toggleServerDeploymentType = (data) => {
        dispatch(
            httpsActions.updateSettings({
                serverDeploymentType: data.value,
            }),
        );
    };

    const sshUserEnabled = defaultSSHUserEnable || !!settings.sshUser.userfulKeys;
    const sshUserToggleDisabled = defaultSSHUserEnable;

    return (
        <>
            <Form className="httpsContainer" id={formId} onSubmit={handleSubmit} onKeyPress={handleKeyPress}>
                <div>{t("InitialSetup.frontendURLDescription")}</div>

                <Form.Group className="keyForm" controlId="url">
                    <Form.Label className="publicKeyLabel">{t("CommonUI.Config.Https.url")}</Form.Label>
                    <Form.Control
                        type="input"
                        placeholder={t("CommonUI.Config.Https.urlPlaceholder")}
                        value={settings.frontEndUrl}
                        isInvalid={!!errors.frontEndUrl}
                        name="frontEndUrl"
                        onChange={onChange}
                        autoFocus
                    />
                    <Form.Control.Feedback type="invalid">{t(errors.frontEndUrl)}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="keyForm" controlId="organization">
                    <Form.Label className="publicKeyLabel">{t("CommonUI.Config.organization")}</Form.Label>
                    <Form.Control
                        type="input"
                        value={settings.organization}
                        name="organization"
                        onChange={onChange}
                        autoFocus
                    />
                </Form.Group>

                <FormBlock>
                    {/* {!isInit && !sshUserEnabled && <Note
                        id="warning" variant="warning"
                        title={t("InitialSetup.defaultSSHUserWarning")}
                        description={t("InitialSetup.defaultSSHUserWarningDesc")} />} */}

                    <OptionContainer
                        title={t("InitialSetup.defaultSSHUserTitle")}
                        description={t("InitialSetup.defaultSSHUserDescription")}
                        type="checkbox"
                        disabled={sshUserToggleDisabled}
                        value={sshUserEnabled ? "selected" : "unselected"}
                        onClick={(e) => {
                            e.stopPropagation();

                            dispatch(
                                httpsActions.updateSettings({
                                    sshUser: {
                                        ...settings.sshUser,
                                        id: settings.sshUser.userfulKeys ? { value: "NOT_SET" } : { value: systemID },
                                        username: settings.sshUser.userfulKeys ? "" : "userful",
                                        userfulKeys: !settings.sshUser.userfulKeys,
                                    },
                                }),
                            );
                        }}
                        id={"defaultSSHUserTitle"}
                    />
                </FormBlock>

                <div className="overallHTTPContainer">
                    <div className="overallHTTPCertDiv">
                        <div className="selfSignedCertDiv">
                            <Form.Check
                                aria-label="selfSigned"
                                id="selfSigned"
                                onChange={onToggleAutoGenerateCert}
                                name="selfSigned"
                                checked={settings.selfSigned}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <div className="selfSignedCertTexDiv">
                                <div className="selfSignedCertHead">{t("InitialSetup.certHead")}</div>
                                <div className="selfSignedCertDes">{t("InitialSetup.certDes")}</div>
                            </div>
                        </div>

                        {!settings.selfSigned && (
                            <>
                                <div className="publicKeyDiv">
                                    <Form.Group className="keyForm" controlId="cert">
                                        <Form.Label className="publicKeyLabel">
                                            {t("CommonUI.Config.Https.cert")}
                                        </Form.Label>
                                        <div className="inputUpload">
                                            <Form.Control
                                                as="input"
                                                name="cert"
                                                value={getShowValue(settings.cert, certFocus)}
                                                isInvalid={!!errors.cert}
                                                onChange={onChange}
                                                ref={certRef}
                                                onFocus={onFocus}
                                                onBlur={onBlur}
                                                disabled={settings.selfSigned}
                                            />
                                            <label className="httpsFileUpload">
                                                <input
                                                    type="file"
                                                    name="cert"
                                                    onChange={handleFileUpload}
                                                    disabled={settings.selfSigned}
                                                />
                                                <FontAwesomeIcon icon={faUpload} />
                                            </label>
                                        </div>
                                        <Form.Control.Feedback type="invalid">{t(errors.cert)}</Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <div className="publicKeyDiv">
                                    <Form.Group className="keyForm" controlId="key">
                                        <Form.Label className="publicKeyLabel">
                                            {t("CommonUI.Config.Https.key")}
                                        </Form.Label>
                                        <div className="inputUpload">
                                            <Form.Control
                                                as="input"
                                                name="key"
                                                value={getShowValue(settings.key, keyfocus)}
                                                isInvalid={!!errors.key}
                                                onChange={onChange}
                                                ref={keyRef}
                                                onFocus={onFocus}
                                                onBlur={onBlur}
                                                disabled={settings.selfSigned}
                                            />
                                            <label className="httpsFileUpload">
                                                <input
                                                    type="file"
                                                    name="key"
                                                    onChange={handleFileUpload}
                                                    disabled={settings.selfSigned}
                                                />
                                                <FontAwesomeIcon icon={faUpload} />
                                            </label>
                                        </div>
                                        <Form.Control.Feedback type="invalid">{t(errors.key)}</Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div className="deploymentTypeContainer">
                    <Form.Group className="keyForm" controlId="deploymentType">
                        <Form.Label className="publicKeyLabel">{t("CommonUI.Config.deploymentType")}</Form.Label>
                        <span>{t("CommonUI.Config.deploymentTypeDesc")}</span>
                    </Form.Group>
                    <Form.Group className="keyForm" controlId="deploymentType">
                        <Form.Label className="publicKeyLabel">
                            {t("CommonUI.Config.deploymentType")}
                            <HelpInfo title={t("CommonUI.Config.deploymentTypeInfo")} icon={faCircleQuestion} />
                        </Form.Label>
                        <Select
                            options={deploymentTypeOptions}
                            onChange={toggleServerDeploymentType}
                            value={selectedDeployment}
                            styles={customStyles}
                            components={{ IndicatorSeparator: () => null }}
                            instanceId="mapper-fps-select"
                            inputId="mapper-fps-select"
                        />
                    </Form.Group>
                </div>
            </Form>

            {popUpError && (
                <UserfulConfirmationDialog
                    show={true}
                    noCloseButton
                    icon={{ icon: faWarning, color: "#D13E2C" }}
                    title="ERROR"
                    replaceBody={<p>{popUpError}</p>}
                    replaceFooter={
                        <>
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    dispatch(httpsActions.clearPopUpError());
                                }}
                            >
                                {t("Shared.cancel")}
                            </Button>
                        </>
                    }
                />
            )}
        </>
    );
}
